// Batch review + per-row editing + sequential upload screen for ZIP uploads.
// Used by both SubmitPage (public submission) and AdminUploadForm (direct upload).
//
// Props:
// - rows, setRows           : controlled state from parent
// - errors                  : pre-flight extractor errors to surface
// - mode: "public"|"admin"  : controls which extra fields render per row
// - stations, moods         : admin lookup arrays (for select dropdowns)
// - onCancel()              : drop the ZIP and return to single-song form
// - uploader(row) => Promise<{ok, error}>
//                           : parent-supplied uploader (one per row).
//                             Lets the public form POST /submissions and admin POST /admin/tracks
//                             with the right payload per mode.
// - onAllDone()             : called once after the final upload finishes (parent resets UI)

import React, { useMemo, useState } from "react";
import { Trash2, Image as ImageIcon, Sparkles, AlertCircle, CheckCircle2, Loader2, Upload, X } from "lucide-react";
import { MAIN_GENRES } from "../lib/taxonomy";

const APPLY_TO_ALL_FIELDS_PUBLIC = [
  { key: "artist", label: "Artist" },
  { key: "genre", label: "Genre" },
];
const APPLY_TO_ALL_FIELDS_ADMIN = [
  { key: "artist", label: "Artist" },
  { key: "genre", label: "Genre" },
  { key: "station", label: "Station" },
  { key: "mood", label: "Mood" },
  { key: "energy", label: "Energy" },
];

export default function BatchUploadReview({
  rows,
  setRows,
  errors,
  mode,
  stations = [],
  moods = [],
  onCancel,
  uploader,
  onAllDone,
}) {
  const isAdmin = mode === "admin";
  const applyFields = isAdmin ? APPLY_TO_ALL_FIELDS_ADMIN : APPLY_TO_ALL_FIELDS_PUBLIC;
  const [bulkValue, setBulkValue] = useState({});
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState({}); // { rowId: "ok"|"fail"|"uploading"|"pending" }
  const [progressErrors, setProgressErrors] = useState({}); // rowId -> message

  const includedRows = useMemo(() => rows.filter((r) => r.include), [rows]);
  const readyRows = useMemo(() => rows.filter((r) => r.include && r.title.trim() && r.artist.trim()), [rows]);
  const needsAttention = useMemo(() => rows.filter((r) => r.include && (!r.title.trim() || !r.artist.trim())), [rows]);

  function patchRow(id, patch) {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  }

  function applyToAll(field) {
    const val = bulkValue[field];
    if (val === undefined || val === "") return;
    setRows((prev) => prev.map((r) => (r.include ? { ...r, [field]: val } : r)));
  }

  function applyCoverToAll(file) {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    setRows((prev) => prev.map((r) => (r.include ? { ...r, coverFile: file, coverPreview: previewUrl, coverSource: "applied-to-all" } : r)));
  }

  function setRowCover(id, file) {
    if (!file) return;
    const previewUrl = URL.createObjectURL(file);
    patchRow(id, { coverFile: file, coverPreview: previewUrl, coverSource: "user-override" });
  }

  function selectAll(value) {
    setRows((prev) => prev.map((r) => ({ ...r, include: value })));
  }

  async function uploadAll() {
    if (busy) return;
    if (readyRows.length === 0) return;
    setBusy(true);
    const newProgress = {};
    const newErrors = {};
    rows.forEach((r) => {
      if (r.include && r.title.trim() && r.artist.trim()) newProgress[r.id] = "pending";
    });
    setProgress({ ...newProgress });

    for (const row of rows) {
      if (!row.include) continue;
      if (!row.title.trim() || !row.artist.trim()) continue; // skip rows that need attention
      newProgress[row.id] = "uploading";
      setProgress({ ...newProgress });
      try {
        const res = await uploader(row);
        if (res?.ok) {
          newProgress[row.id] = "ok";
        } else {
          newProgress[row.id] = "fail";
          newErrors[row.id] = res?.error || "Upload failed";
        }
      } catch (e) {
        newProgress[row.id] = "fail";
        newErrors[row.id] = e?.message || String(e);
      }
      setProgress({ ...newProgress });
      setProgressErrors({ ...newErrors });
    }

    setBusy(false);
    onAllDone?.();
  }

  return (
    <div className="space-y-4" data-testid="batch-upload-review">
      {/* Header / summary */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-sm font-medium text-[#FDFBF7]">
            ZIP batch upload — {rows.length} song{rows.length === 1 ? "" : "s"} found
          </div>
          <div className="text-xs text-[#A1A4AB]">
            {readyRows.length} ready · {needsAttention.length} need attention · {rows.length - includedRows.length} excluded
          </div>
        </div>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="text-xs overline px-3 py-1.5 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059] hover:text-[#FDFBF7] inline-flex items-center gap-1.5"
          data-testid="batch-cancel-btn"
        >
          <X className="w-3.5 h-3.5" strokeWidth={1.5} /> Discard ZIP
        </button>
      </div>

      {/* Pre-flight errors */}
      {errors && errors.length > 0 && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200 space-y-1">
          {errors.map((m, i) => (
            <div key={i} className="flex items-start gap-2">
              <AlertCircle className="w-3.5 h-3.5 mt-0.5" strokeWidth={1.5} /> {m}
            </div>
          ))}
        </div>
      )}

      {/* Batch actions */}
      <div className="rounded-lg border border-[#22262E] bg-[#111317] p-3 space-y-3">
        <div className="text-xs overline text-[#C5A059]">Batch actions</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="flex gap-2 items-center">
            <button type="button" onClick={() => selectAll(true)} disabled={busy} className="text-xs overline px-3 py-1 rounded-full border border-[#22262E] text-[#FDFBF7] hover:border-[#C5A059]" data-testid="batch-select-all">Select all</button>
            <button type="button" onClick={() => selectAll(false)} disabled={busy} className="text-xs overline px-3 py-1 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-[#C5A059]" data-testid="batch-deselect-all">Deselect all</button>
          </div>
          <label className="flex items-center gap-2 text-xs text-[#A1A4AB]">
            <span className="whitespace-nowrap">Apply same cover to all:</span>
            <input
              type="file"
              accept="image/*"
              disabled={busy}
              onChange={(e) => applyCoverToAll(e.target.files?.[0] || null)}
              className="text-xs text-[#A1A4AB] file:mr-2 file:py-1 file:px-3 file:text-xs file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E]"
              data-testid="batch-apply-cover-all"
            />
          </label>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-2">
          {applyFields.map((f) => (
            <div key={f.key} className="flex gap-2 items-center">
              {f.key === "station" && isAdmin ? (
                <select
                  className="input-lux text-xs flex-1"
                  value={bulkValue[f.key] || ""}
                  onChange={(e) => setBulkValue({ ...bulkValue, [f.key]: e.target.value })}
                  data-testid={`batch-bulk-${f.key}`}
                >
                  <option value="">Choose station…</option>
                  {stations.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              ) : f.key === "mood" && isAdmin ? (
                <select
                  className="input-lux text-xs flex-1"
                  value={bulkValue[f.key] || ""}
                  onChange={(e) => setBulkValue({ ...bulkValue, [f.key]: e.target.value })}
                  data-testid={`batch-bulk-${f.key}`}
                >
                  <option value="">Choose mood…</option>
                  {moods.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : f.key === "energy" && isAdmin ? (
                <select
                  className="input-lux text-xs flex-1"
                  value={bulkValue[f.key] || ""}
                  onChange={(e) => setBulkValue({ ...bulkValue, [f.key]: parseInt(e.target.value, 10) })}
                  data-testid={`batch-bulk-${f.key}`}
                >
                  <option value="">Choose energy…</option>
                  {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input
                  className="input-lux text-xs flex-1"
                  placeholder={f.label}
                  value={bulkValue[f.key] || ""}
                  onChange={(e) => setBulkValue({ ...bulkValue, [f.key]: e.target.value })}
                  data-testid={`batch-bulk-${f.key}`}
                />
              )}
              <button
                type="button"
                onClick={() => applyToAll(f.key)}
                disabled={busy}
                className="text-xs overline px-3 py-1 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059] hover:text-[#060709] whitespace-nowrap"
                data-testid={`batch-apply-${f.key}`}
              >
                Apply to all
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Per-row review */}
      <div className="space-y-3">
        {rows.map((row) => {
          const status = progress[row.id];
          const rowErr = progressErrors[row.id];
          const titleErr = row.include && !row.title.trim();
          const artistErr = row.include && !row.artist.trim();
          return (
            <div
              key={row.id}
              className={`rounded-lg border p-3 ${row.include ? "border-[#22262E] bg-[#111317]" : "border-[#22262E]/50 bg-[#0A0B0E] opacity-60"}`}
              data-testid={`batch-row-${row.id}`}
            >
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  checked={row.include}
                  onChange={(e) => patchRow(row.id, { include: e.target.checked })}
                  disabled={busy}
                  className="accent-[#C5A059] mt-2"
                  data-testid={`batch-row-include-${row.id}`}
                />
                {row.coverPreview ? (
                  <img src={row.coverPreview} alt={`${row.title || row.filename} artwork`} className="w-14 h-14 rounded-md object-cover border border-[#22262E]" />
                ) : (
                  <div className="w-14 h-14 rounded-md bg-[#1A1D24] border border-[#22262E] flex items-center justify-center text-[#6C707A]">
                    <ImageIcon className="w-5 h-5" strokeWidth={1.5} />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="text-xs text-[#6C707A] truncate max-w-md" title={row.filename}>{row.filename}</div>
                    <div className="text-xs text-[#6C707A] flex items-center gap-2">
                      <span>cover: {row.coverSource}</span>
                      <span>·</span>
                      <span>lyrics: {row.hasEmbeddedLyrics ? "yes" : "no"}</span>
                      {status && (
                        <>
                          <span>·</span>
                          {status === "uploading" && <span className="inline-flex items-center gap-1 text-[#C5A059]"><Loader2 className="w-3 h-3 animate-spin" /> uploading</span>}
                          {status === "ok" && <span className="inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-3 h-3" /> uploaded</span>}
                          {status === "fail" && <span className="inline-flex items-center gap-1 text-red-400"><AlertCircle className="w-3 h-3" /> failed</span>}
                          {status === "pending" && <span className="text-[#A1A4AB]">queued</span>}
                        </>
                      )}
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-2">
                    <input
                      className={`input-lux text-sm ${titleErr ? "border-red-400/60" : ""}`}
                      placeholder="Title *"
                      value={row.title}
                      onChange={(e) => patchRow(row.id, { title: e.target.value })}
                      disabled={busy}
                      data-testid={`batch-row-title-${row.id}`}
                    />
                    <input
                      className={`input-lux text-sm ${artistErr ? "border-red-400/60" : ""}`}
                      placeholder="Artist *"
                      value={row.artist}
                      onChange={(e) => patchRow(row.id, { artist: e.target.value })}
                      disabled={busy}
                      data-testid={`batch-row-artist-${row.id}`}
                    />
                    <input
                      className="input-lux text-sm"
                      placeholder="Genre (free text)"
                      value={row.genre}
                      onChange={(e) => patchRow(row.id, { genre: e.target.value })}
                      disabled={busy}
                    />
                    <select
                      className="input-lux text-sm"
                      value={row.main_genre || ""}
                      onChange={(e) => patchRow(row.id, { main_genre: e.target.value })}
                      disabled={busy}
                      data-testid={`batch-row-main-genre-${row.id}`}
                    >
                      <option value="">Main genre — auto-map</option>
                      {MAIN_GENRES.map((g) => <option key={g} value={g}>{g}</option>)}
                    </select>
                    {isAdmin ? (
                      <select
                        className="input-lux text-sm"
                        value={row.station || ""}
                        onChange={(e) => patchRow(row.id, { station: e.target.value })}
                        disabled={busy}
                      >
                        <option value="">Station (none)</option>
                        {stations.map((s) => <option key={s} value={s}>{s}</option>)}
                      </select>
                    ) : (
                      <input
                        className="input-lux text-sm"
                        placeholder="Album / scripture (optional)"
                        value={row.album}
                        onChange={(e) => patchRow(row.id, { album: e.target.value })}
                        disabled={busy}
                      />
                    )}
                    {isAdmin && (
                      <>
                        <select
                          className="input-lux text-sm"
                          value={row.mood || ""}
                          onChange={(e) => patchRow(row.id, { mood: e.target.value })}
                          disabled={busy}
                        >
                          <option value="">Mood (none)</option>
                          {moods.map((m) => <option key={m} value={m}>{m}</option>)}
                        </select>
                        <select
                          className="input-lux text-sm"
                          value={row.energy || 3}
                          onChange={(e) => patchRow(row.id, { energy: parseInt(e.target.value, 10) })}
                          disabled={busy}
                        >
                          {[1, 2, 3, 4, 5].map((n) => <option key={n} value={n}>Energy {n}</option>)}
                        </select>
                      </>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <label className="text-xs text-[#A1A4AB] inline-flex items-center gap-2">
                      <span>Override cover:</span>
                      <input
                        type="file"
                        accept="image/*"
                        disabled={busy}
                        onChange={(e) => setRowCover(row.id, e.target.files?.[0] || null)}
                        className="text-xs text-[#A1A4AB] file:mr-2 file:py-1 file:px-2 file:text-xs file:rounded-full file:border-0 file:bg-[#1A1D24] file:text-[#FDFBF7] file:border file:border-[#22262E]"
                      />
                    </label>
                    <button
                      type="button"
                      onClick={() => setRows((prev) => prev.filter((r) => r.id !== row.id))}
                      disabled={busy}
                      title="Remove this song from the batch"
                      className="ml-auto text-xs overline px-3 py-1 rounded-full border border-[#22262E] text-[#A1A4AB] hover:border-red-500 hover:text-red-400 inline-flex items-center gap-1"
                      data-testid={`batch-row-remove-${row.id}`}
                    >
                      <Trash2 className="w-3 h-3" strokeWidth={1.5} /> Remove
                    </button>
                  </div>
                  {(titleErr || artistErr) && (
                    <div className="text-xs text-amber-300 inline-flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3" strokeWidth={1.5} />
                      {titleErr && artistErr ? "Needs title and artist" : titleErr ? "Needs title" : "Needs artist"}
                    </div>
                  )}
                  {rowErr && (
                    <div className="text-xs text-red-400">{rowErr}</div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Submit */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="text-xs text-[#A1A4AB]">
          {readyRows.length > 0 ? (
            <>About to upload <strong className="text-[#FDFBF7]">{readyRows.length}</strong> song{readyRows.length === 1 ? "" : "s"}.</>
          ) : (
            <>Fill in title and artist for at least one song to proceed.</>
          )}
        </div>
        <button
          type="button"
          disabled={busy || readyRows.length === 0}
          onClick={uploadAll}
          className="btn-primary"
          data-testid="batch-upload-all-btn"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" strokeWidth={1.5} />}
          {busy ? "Uploading…" : `Upload ${readyRows.length} song${readyRows.length === 1 ? "" : "s"}`}
        </button>
      </div>
      {busy && (
        <div className="text-xs text-[#6C707A]">Don't close this tab until all uploads finish — each song is sent as a separate track.</div>
      )}
    </div>
  );
}

// Build a Title-Cased badge label from a status string (helper for parents if needed)
export function rowStatusLabel(status) {
  if (!status) return "";
  if (status === "ok") return "Uploaded";
  if (status === "fail") return "Failed";
  if (status === "uploading") return "Uploading";
  return "Queued";
}

// Re-export a Sparkles icon name to keep one import in parents
export const BatchSparklesIcon = Sparkles;
